// ── API Keys Settings Tab ─────────────────────────────────────
// Create, view, and revoke API keys for MCP servers and CLI tools.

import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../api';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadKeys = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/keys`);
      if (res.ok) setKeys(await res.json());
    } catch (err) {
      console.error('Failed to load API keys:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await authFetch(`${API_URL}/api/keys`, {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (res.ok) {
        const result = await res.json();
        setCreatedKey(result.key);
        setNewName('');
        loadKeys();
      }
    } catch (err) {
      console.error('Failed to create key:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Revoke this API key? Any integrations using it will stop working.')) return;
    try {
      const res = await authFetch(`${API_URL}/api/keys/${keyId}`, { method: 'DELETE' });
      if (res.ok) loadKeys();
    } catch (err) {
      console.error('Failed to revoke key:', err);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="settings-section">
      <h2 className="settings-section-title">API Keys</h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '0 0 16px' }}>
        Use API keys to connect ThoughtClaw with Claude Desktop, Claude Code, or other MCP-compatible tools.
      </p>

      {/* Key creation */}
      <div className="settings-card">
        <div className="settings-field">
          <label className="settings-field-label">Create New Key</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="settings-input"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Key name (e.g. Claude Desktop)"
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              style={{ flex: 1 }}
            />
            <button
              className="settings-save-btn"
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>

        {/* Show newly created key */}
        {createdKey && (
          <div style={{
            marginTop: 12,
            padding: 12,
            background: 'var(--bg-tertiary, #1a1a2e)',
            borderRadius: 8,
            border: '1px solid var(--accent, #00d4ff)',
          }}>
            <div style={{ fontSize: 12, color: 'var(--accent, #00d4ff)', marginBottom: 8, fontWeight: 600 }}>
              Copy this key now — you won't see it again
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code style={{
                flex: 1,
                padding: '8px 10px',
                background: 'var(--bg-primary, #0a0a1a)',
                borderRadius: 4,
                fontSize: 12,
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                color: 'var(--text-primary, #fff)',
              }}>
                {createdKey}
              </code>
              <button className="settings-save-btn" onClick={handleCopy} style={{ whiteSpace: 'nowrap' }}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8 }}>
              Add to Claude Desktop config: <code style={{ fontSize: 11 }}>
                {`"env": { "THOUGHTCLAW_API_KEY": "${createdKey.slice(0, 11)}..." }`}
              </code>
            </div>
            <button
              onClick={() => setCreatedKey(null)}
              style={{ marginTop: 8, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* Existing keys */}
      <div className="settings-card" style={{ marginTop: 16 }}>
        <div className="settings-field-label" style={{ marginBottom: 12 }}>Your Keys</div>
        {loading ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
        ) : keys.length === 0 ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No API keys yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {keys.map(k => (
              <div key={k.id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                background: 'var(--bg-tertiary, #1a1a2e)',
                borderRadius: 6,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary, #fff)' }}>{k.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                    <code>{k.keyPrefix}</code> — created {new Date(k.createdAt).toLocaleDateString()}
                    {k.lastUsedAt && <> — last used {new Date(k.lastUsedAt).toLocaleDateString()}</>}
                  </div>
                </div>
                <button
                  onClick={() => handleRevoke(k.id)}
                  style={{
                    background: 'none',
                    border: '1px solid var(--error, #ff4757)',
                    color: 'var(--error, #ff4757)',
                    borderRadius: 4,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    fontSize: 11,
                  }}
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Setup instructions */}
      <div className="settings-card" style={{ marginTop: 16 }}>
        <div className="settings-field-label" style={{ marginBottom: 8 }}>Claude Desktop Setup</div>
        <pre style={{
          background: 'var(--bg-primary, #0a0a1a)',
          padding: 12,
          borderRadius: 6,
          fontSize: 11,
          fontFamily: 'monospace',
          color: 'var(--text-secondary)',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
        }}>
{`// ~/Library/Application Support/Claude/claude_desktop_config.json
{
  "mcpServers": {
    "thoughtclaw": {
      "command": "node",
      "args": ["<path-to>/mcp/index.js"],
      "env": {
        "THOUGHTCLAW_API_KEY": "tc_...",
        "THOUGHTCLAW_API_URL": "https://thoughtclaw.app"
      }
    }
  }
}`}
        </pre>
      </div>
    </div>
  );
}
